import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { collection, query, onSnapshot, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Decision } from '../../types';
import { Vote, Plus, Clock, CheckCircle, Users } from 'lucide-react';

const DecisionHub: React.FC = () => {
  const { userProfile } = useAuth();
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newDecision, setNewDecision] = useState({
    title: '',
    description: '',
    options: ['', '']
  });

  useEffect(() => {
    const q = query(collection(db, 'decisions'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const decisionsData: Decision[] = [];
      querySnapshot.forEach((doc) => {
        decisionsData.push({ id: doc.id, ...doc.data() } as Decision);
      });
      setDecisions(decisionsData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
    });

    return () => unsubscribe();
  }, []);

  const handleCreateDecision = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile || userProfile.role !== 'admin') return;

    try {
      const decisionData = {
        title: newDecision.title,
        description: newDecision.description,
        options: newDecision.options.filter(option => option.trim() !== ''),
        votes: {},
        votedUsers: [],
        createdBy: userProfile.uid,
        createdByName: userProfile.displayName || userProfile.email,
        createdAt: new Date(),
        isActive: true,
        isAnonymous: true
      };

      await addDoc(collection(db, 'decisions'), decisionData);
      setNewDecision({ title: '', description: '', options: ['', ''] });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating decision:', error);
    }
  };

  const handleVote = async (decisionId: string, optionIndex: number) => {
    if (!userProfile) return;

    try {
      const decision = decisions.find(d => d.id === decisionId);
      if (!decision) return;

      // Check if user has already voted
      if (decision.votedUsers && decision.votedUsers.includes(userProfile.uid)) {
        alert('You have already voted on this decision.');
        return;
      }

      const updatedVotes = { ...decision.votes };
      const updatedVotedUsers = [...(decision.votedUsers || [])];

      // Add new vote (anonymous)
      if (!updatedVotes[optionIndex]) {
        updatedVotes[optionIndex] = [];
      }
      updatedVotes[optionIndex].push(userProfile.uid);
      updatedVotedUsers.push(userProfile.uid);

      await updateDoc(doc(db, 'decisions', decisionId), { 
        votes: updatedVotes,
        votedUsers: updatedVotedUsers
      });
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  const hasUserVoted = (decision: Decision): boolean => {
    if (!userProfile) return false;
    return decision.votedUsers?.includes(userProfile.uid) || false;
  };

  const getVoteCount = (decision: Decision, optionIndex: number): number => {
    return decision.votes[optionIndex]?.length || 0;
  };

  const getTotalVotes = (decision: Decision): number => {
    return Object.values(decision.votes).reduce((total, voters) => total + voters.length, 0);
  };

  const addOption = () => {
    setNewDecision(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const updateOption = (index: number, value: string) => {
    setNewDecision(prev => ({
      ...prev,
      options: prev.options.map((option, i) => i === index ? value : option)
    }));
  };

  const removeOption = (index: number) => {
    if (newDecision.options.length > 2) {
      setNewDecision(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index)
      }));
    }
  };

  return (
    <DashboardLayout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          {/* Header */}
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Decision Hub</h1>
              <p className="mt-1 text-sm text-gray-600">
                Participate in company decisions and view voting results
              </p>
            </div>
            {userProfile?.role === 'admin' && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
              >
                <Plus className="mr-2" size={20} />
                Create Decision
              </button>
            )}
          </div>

          {/* Create Decision Form */}
          {showCreateForm && userProfile?.role === 'admin' && (
            <div className="mb-8 bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Decision</h3>
              <form onSubmit={handleCreateDecision}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Decision Title
                  </label>
                  <input
                    type="text"
                    required
                    value={newDecision.title}
                    onChange={(e) => setNewDecision(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter decision title"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={newDecision.description}
                    onChange={(e) => setNewDecision(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Describe the decision that needs to be made"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Options
                  </label>
                  {newDecision.options.map((option, index) => (
                    <div key={index} className="flex mb-2">
                      <input
                        type="text"
                        required
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder={`Option ${index + 1}`}
                      />
                      {newDecision.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          className="ml-2 px-3 py-2 text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addOption}
                    className="text-primary-600 hover:text-primary-800 text-sm"
                  >
                    + Add Option
                  </button>
                </div>
                <div className="flex space-x-4">
                  <button
                    type="submit"
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Create Decision
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Decisions List */}
          <div className="space-y-6">
            {decisions.length === 0 ? (
              <div className="text-center py-12">
                <Vote className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No decisions yet</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {userProfile?.role === 'admin' 
                    ? 'Create the first decision for your team to vote on.'
                    : 'Check back later for decisions to vote on.'
                  }
                </p>
              </div>
            ) : (
              decisions.map((decision) => {
                const hasVoted = hasUserVoted(decision);
                const totalVotes = getTotalVotes(decision);
                
                return (
                  <div key={decision.id} className="bg-white shadow rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {decision.title}
                        </h3>
                        <p className="text-gray-600 mb-4">{decision.description}</p>
                        <div className="flex items-center text-sm text-gray-500">
                          <Clock className="mr-1" size={16} />
                          Created {decision.createdAt.toLocaleDateString()} by {decision.createdByName}
                          <Users className="ml-4 mr-1" size={16} />
                          {totalVotes} votes
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {decision.isAnonymous && (
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                            Anonymous
                          </span>
                        )}
                        {decision.isActive ? (
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                            Active
                          </span>
                        ) : (
                          <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium">
                            Closed
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      {decision.options.map((option, index) => {
                        const voteCount = getVoteCount(decision, index);
                        const percentage = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;

                        return (
                          <div key={index} className="relative">
                            <button
                              onClick={() => decision.isActive && !hasVoted && handleVote(decision.id, index)}
                              disabled={!decision.isActive || hasVoted}
                              className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                                hasVoted
                                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                                  : decision.isActive
                                  ? 'border-gray-200 hover:border-primary-300 hover:bg-primary-50 cursor-pointer'
                                  : 'border-gray-200 cursor-not-allowed opacity-60'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <span className="font-medium text-gray-900">{option}</span>
                                </div>
                                <div className="text-sm text-gray-600">
                                  {voteCount} votes ({percentage.toFixed(1)}%)
                                </div>
                              </div>
                              {totalVotes > 0 && (
                                <div className="mt-2 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="h-2 rounded-full bg-primary-600"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {hasVoted && (
                      <div className="mt-4 p-3 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-800 flex items-center">
                          <CheckCircle className="mr-2" size={16} />
                          You have voted on this decision (anonymous voting)
                        </p>
                      </div>
                    )}

                    {decision.isActive && !hasVoted && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          Click on an option above to cast your vote. Voting is anonymous.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DecisionHub;